/**
 * AutoLearningBanner - CESM OSINT System v2.6.0
 * Bannière d'auto-apprentissage affichée une fois par session
 * pendant les scans ou la génération de Magic Reports
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Brain, Lightbulb, Sparkles, X, ChevronRight, BookOpen, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AutoLearningBanner = ({ 
  token, 
  isActive, // true quand un scan ou magic report est en cours
  scanTopic,
  scanResults = [],
  context = 'scan', // 'scan' ou 'magic_report'
  onLearnClick // callback quand l'utilisateur veut apprendre
}) => {
  const [suggestion, setSuggestion] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [gaps, setGaps] = useState([]);

  // Récupérer la suggestion d'auto-apprentissage
  const fetchSuggestion = useCallback(async () => {
    if (!isActive || isDismissed || !token) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${API}/api/admin/auto-learning/suggestion`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          scan_topic: scanTopic || 'analyse OSINT',
          scan_results: scanResults.slice(0, 10),
          context
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        setSuggestion(data);
        setGaps(data.gaps_count > 0 ? data.prompts : []);
      } else if (data.status === 'already_shown') {
        // Ne rien afficher, déjà montré cette session
        setSuggestion(null);
      }
    } catch (error) {
      console.error('Auto-learning error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isActive, isDismissed, token, scanTopic, scanResults, context]);

  // Récupérer les lacunes détectées
  const fetchGaps = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API}/api/admin/auto-learning/gaps`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.status === 'success') {
        setGaps(data.gaps);
      }
    } catch (error) {
      console.error('Error fetching gaps:', error);
    }
  }, [token]);

  // Déclencher au démarrage du scan
  useEffect(() => {
    if (isActive && !suggestion && !isDismissed) {
      // Attendre un peu pour laisser le scan démarrer
      const timer = setTimeout(fetchSuggestion, 2000);
      return () => clearTimeout(timer);
    }
  }, [isActive, suggestion, isDismissed, fetchSuggestion]);

  // Handler pour le clic sur un prompt
  const handlePromptClick = (prompt) => {
    if (onLearnClick) {
      onLearnClick(prompt);
    }
    toast.success('Redirection vers le panneau d\'apprentissage');
    setIsDismissed(true);
  };

  // Handler pour fermer
  const handleDismiss = () => {
    setIsDismissed(true);
    setSuggestion(null);
  };

  // Ne rien afficher si pas de suggestion ou déjà fermé
  if (!suggestion || isDismissed || !isActive) {
    return null;
  }

  return (
    <div 
      className="fixed bottom-4 right-4 z-50 max-w-md animate-slide-up"
      data-testid="auto-learning-banner"
    >
      <div className="bg-gradient-to-br from-purple-900/95 to-indigo-900/95 backdrop-blur-sm border border-purple-500/50 rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-purple-500/30">
          <div className="flex items-center gap-2">
            <div className="bg-yellow-500/20 p-1.5 rounded-full">
              <Lightbulb className="w-4 h-4 text-yellow-400 animate-pulse" />
            </div>
            <span className="font-mono text-purple-300 text-sm font-bold">
              Auto-Apprentissage
            </span>
            {suggestion.gaps_count > 0 && (
              <span className="bg-amber-500/20 text-amber-300 text-xs px-2 py-0.5 rounded-full">
                {suggestion.gaps_count} lacune{suggestion.gaps_count > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <button
            onClick={handleDismiss}
            className="text-purple-400 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-mono text-white/90 text-sm">
                {suggestion.message}
              </p>
              {suggestion.detail && (
                <p className="font-mono text-purple-300/80 text-xs mt-1">
                  {suggestion.detail}
                </p>
              )}
            </div>
          </div>

          {/* Prompts */}
          <div className="flex flex-wrap gap-2 mt-3">
            {suggestion.prompts?.map((prompt, idx) => (
              <button
                key={idx}
                onClick={() => handlePromptClick(prompt)}
                className="flex items-center gap-1.5 bg-purple-800/50 hover:bg-purple-700/50 text-purple-200 hover:text-white px-3 py-1.5 rounded text-xs font-mono transition-colors border border-purple-500/30"
              >
                <ChevronRight className="w-3 h-3" />
                {prompt}
              </button>
            ))}
          </div>

          {/* Footer info */}
          <div className="flex items-center justify-between pt-2 border-t border-purple-500/20 mt-3">
            <span className="text-purple-400/60 text-xs font-mono">
              {context === 'scan' ? 'Pendant le scan' : 'Magic Report'}
            </span>
            <span className="text-purple-400/60 text-xs font-mono">
              Une fois par session
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoLearningBanner;
